<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { definePage } from 'vue-router/auto'
import { Icon } from '@iconify/vue'
import { Card } from '~/components/ui/card'
import { buttonVariants } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import { useVerifyEmailService } from '~/services/auth/useVerifyEmailService'

definePage({
  meta: {
    requiresAuth: false,
    allowAuthenticated: true,
  },
})

const { verificationMessage, route, router, verifyUserLink } = useVerifyEmailService()
const isChecking = ref(true)

onMounted(async () => {
  if (route.query.queryURL) {
    await verifyUserLink(route.query.queryURL)
    isChecking.value = false
  } else {
    // The 404 route is the `/:all(.*)` catch-all, so its `all` param is
    // required — pushing the name alone throws `Missing required param "all"`.
    await router.push({ name: '404', params: { all: '404' } })
  }
})

// The result replaces the loading status: move focus to its heading so it is announced.
const vFocus = { mounted: (el: HTMLElement) => el.focus() }
</script>

<template>
  <Card class="w-full max-w-[400px] px-6 pb-6 pt-7 shadow-sm">
    <p
      v-if="isChecking"
      role="status"
      class="flex items-center gap-2 text-sm text-muted-foreground"
    >
      <Icon icon="lucide:loader-circle" class="h-4 w-4 animate-spin" aria-hidden="true" />
      {{ $t('Checking your link…') }}
    </p>

    <div v-else-if="verificationMessage" class="flex flex-col gap-6">
      <span
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"
      >
        <Icon icon="lucide:check" class="h-5 w-5" aria-hidden="true" />
      </span>
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
          {{ $t('Professional portal') }}
        </span>
        <h1
          v-focus
          tabindex="-1"
          class="font-display text-2xl font-semibold leading-[1.2] tracking-[-0.01em] outline-none"
        >
          {{ $t('Account activated') }}
        </h1>
        <p class="text-sm text-muted-foreground">
          {{ $t('Your email address is confirmed. Set a password to sign in.') }}
        </p>
      </div>
      <router-link
        to="/auth/forgot-password"
        :class="cn(buttonVariants({ variant: 'default' }), 'h-10 w-full font-semibold max-sm:h-12')"
      >
        {{ $t('Set password') }}
      </router-link>
    </div>

    <div v-else class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
          {{ $t('Professional portal') }}
        </span>
        <h1
          v-focus
          tabindex="-1"
          class="font-display text-2xl font-semibold leading-[1.2] tracking-[-0.01em] outline-none"
        >
          {{ $t('This link is invalid or has expired') }}
        </h1>
        <p class="text-sm text-muted-foreground">
          {{
            $t(
              'Ask your administrator for a new invitation, or sign in if your account is already active.',
            )
          }}
        </p>
      </div>
      <router-link
        to="/auth/login"
        :class="cn(buttonVariants({ variant: 'outline' }), 'h-10 w-full font-semibold max-sm:h-12')"
      >
        {{ $t('Go to sign in') }}
      </router-link>
    </div>
  </Card>
</template>
